import { HeartHandshake, Wrench, ShieldCheck, Radio, Boxes } from "lucide-react";
import Reveal from "@/components/Reveal";

const SUPPORT_IMG =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/k1zc1jul_image.png";

const PILLARS = [
  {
    icon: Wrench,
    title: "In-house Engineering Team",
    text: "Certified technicians in Smart Digi uniforms — not third-party contractors. Every install, service call, and panel swap is handled by staff we trained.",
  },
  {
    icon: Radio,
    title: "24×7 Remote Monitoring",
    text: "Every screen is health-monitored around the clock. We're alerted the moment a panel drifts on temperature, brightness, or refresh rate — often before you notice.",
  },
  {
    icon: ShieldCheck,
    title: "Scheduled Preventive Maintenance",
    text: "Quarterly on-site service — colour recalibration, ventilation checks, firmware updates, cable inspection — keeping every display looking day-one fresh.",
  },
  {
    icon: Boxes,
    title: "Spare-panel Inventory",
    text: "We stock spares for every product family we deploy. If a module ever fails, a replacement is in the van — no import lead time, no downtime spiral.",
  },
  {
    icon: HeartHandshake,
    title: "Annual Maintenance Contracts",
    text: "Transparent AMC plans with defined SLAs — response times, uptime guarantees, and quarterly reports. Predictable cost, zero surprises.",
  },
];

const SupportServices = () => {
  return (
    <section
      id="support"
      data-testid="support-section"
      className="relative bg-[#F5F7FB] py-24 lg:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Image on top — full width */}
        <Reveal className="mb-16 lg:mb-20">
          <div className="relative rounded-2xl overflow-hidden border hairline shadow-[0_30px_80px_-30px_rgba(11,18,32,0.25)] aspect-[16/7]">
            <img
              src={SUPPORT_IMG}
              alt="Smart Digi engineering team on-site — LED panel maintenance"
              loading="lazy"
              decoding="async"
              className="showcase-img absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </Reveal>

        {/* Heading + intro below the image */}
        <Reveal className="max-w-3xl mb-12 lg:mb-16">
          <div className="eyebrow mb-4">Support & Service</div>
          <h2 className="font-display font-extrabold text-4xl lg:text-5xl leading-[1.05] text-[#0B1220] mb-6">
            Installed once.<br />
            <span className="text-[#00439C]">Supported forever.</span>
          </h2>
          <p className="text-[#475569] text-base lg:text-lg leading-relaxed">
            An LED wall isn't a product — it's a system. We treat every deployment as a
            long-term partnership: monitored around the clock, serviced on schedule, and
            backed by an in-house team that knows the hardware and the site.
          </p>
        </Reveal>

        {/* Pillars grid — 4 on top row, AMC full-width on bottom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E7E5E0] border hairline rounded-2xl overflow-hidden">
          {PILLARS.map(({ icon: Icon, title, text }, i) => (
            <Reveal
              key={title}
              delay={i * 0.06}
              data-testid={`support-pillar-${i}`}
              className={`bg-white p-6 lg:p-8 ${
                i === PILLARS.length - 1 ? "sm:col-span-2 lg:col-span-4" : ""
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#EAF0F8] flex items-center justify-center mb-4">
                <Icon size={18} className="text-[#00439C]" />
              </div>
              <h3 className="font-display font-bold text-[15px] text-[#0B1220] mb-2">
                {title}
              </h3>
              <p className="text-[#4B5563] text-[13.5px] leading-relaxed">{text}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[#475569]">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
            <span className="font-semibold text-[#0B1220]">99.8% uptime</span> across managed screens
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default SupportServices;
